import { cn } from "@/lib/utils";
import {
  Pencil,
  Trash,
  Clock,
  Money,
  ShieldCheck,
  User,
} from "@phosphor-icons/react";
import { formatServiceDate } from "./utils";
import type { Service } from "@/types";

const ROLE_CONFIG = {
  admin: {
    label: "Platforma",
    color: "text-primary",
    bg: "bg-primary/8",
    icon: ShieldCheck,
  },
  therapist: {
    label: "Terapeut",
    color: "text-blue-700",
    bg: "bg-blue-50",
    icon: User,
  },
} as const;

export function ServiceCard({
  service,
  onEdit,
  onDelete,
}: {
  service: Service;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const role = ROLE_CONFIG[service.createdByRole];

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      {/* Content */}
      <div className="flex-1 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <h3
            className="text-base font-semibold text-foreground sm:text-lg"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {service.name}
          </h3>
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium",
              role.bg,
              role.color
            )}
          >
            {role.label}
          </span>
        </div>

        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {service.description}
        </p>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Clock className="size-3.5" />
            {service.durationMinutes} min
          </span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
            <Money className="size-3.5 text-muted-foreground" />
            {service.basePrice ?? service.price} KM
          </span>
        </div>
      </div>

      {/* Actions footer */}
      <div className="flex items-center justify-between border-t border-border/50 px-4 py-3 sm:px-5">
        <span className="text-xs text-muted-foreground">
          {formatServiceDate(service.createdAt)}
        </span>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Uredi uslugu"
          >
            <Pencil className="size-4" />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600"
            aria-label="Obriši uslugu"
          >
            <Trash className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
